import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer.js";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer.js";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import {
  arcgisServicesLandUrl,
  arcgisServicesLandFeatureUrl,
  vectorTileServerNewMinistryUrl,
} from "../../../configs/arcgisServices";
import { getColorAdvertisementType } from "../../../services/genericMethoud/generice";
import { selectedSymbol } from "../../../services/genericMethoud/gisMethouds";
import heartIcon from "../../../images/heart.svg";
import {
  buildOffersWhere,
  FilterSelection,
  MapAd,
  OFFERS_BASE_WHERE,
  HEATMAP_BASE_WHERE,
} from "../utils/offerFilters";

// Global map instances
let map: Map;
let MapViewData: MapView;
let graphicsLayer: GraphicsLayer;
let graphicsLogoLayerlogoAdd: GraphicsLayer;
let graphicsLogoLayer: GraphicsLayer;
let graphicsbuildingsWalletLayer: GraphicsLayer;
let routeLayer: GraphicsLayer;


declare global {
  interface Window {
    mapviews: any;
    map: any;
    F_Land_parcels: any;
    F_Land_parcelsFlashLight: any;
    F_Land_parcelsHeatmap: any;
    F_FavoriteLand: any;
    F_Region: any;
    graphicsbuildingsWalletLayer: any;
    graphicsLogoLayerlogoAdd: any;
    graphicsLogoLayer: any;
  }
}
// Symbol and renderer definitions
const getSymbolType = (typeOfProperty: any) => ({
  type: "simple-marker",
  style: "circle",
  color: getColorAdvertisementType(typeOfProperty),
  size: "15px",
  outline: {
    color: [255, 255, 0],
    width: 0,
  },
});

const uniqueValueRenderer = new UniqueValueRenderer({
  field: "BaseLandType",
  uniqueValueInfos: [
    { value: 1, symbol: getSymbolType(1) },
    { value: 3, symbol: getSymbolType(3) },
    { value: 4, symbol: getSymbolType(4) },
    { value: 5, symbol: getSymbolType(5) },
  ],
});

// Feature layer creation functions
const createFeatureLayer = (url: string, id: string, options: any = {}) => {
  const layerConfig: any = {
    url: url,
    id: id,
    opacity: options.opacity || 1,
    outFields: options.outFields || ["*"],
    definitionExpression: options.definitionExpression,
    minScale: options.minScale,
    maxScale: options.maxScale,
    labelsVisible: options.labelsVisible !== undefined ? options.labelsVisible : true,
  };

  // Only add renderer if it's provided
  if (options.renderer) {
    layerConfig.renderer = options.renderer;
  }

  const layer = new FeatureLayer(layerConfig);
  // console.log("options.id", id);
  // console.log("options.sublayers", options.sublayers);
  if (options.sublayers) {
    return new MapImageLayer({
      url,
      opacity: options.opacity || 1,
      id,
      sublayers: options.sublayers,
      minScale: options.minScale,
    });
  }

  return layer;
};

const initializeFeatureLayers = (mapInstance: Map) => {
  // Region layer
  const f_Region = createFeatureLayer(
    arcgisServicesLandFeatureUrl + "3",
    "F_Region",
    { opacity: 0, labelsVisible: false }
  );
  mapInstance.add(window.F_Region);

  // Land parcels map service
  // const f_Land_parcelsMapService = createFeatureLayer(
  //   arcgisServicesLandUrl,
  //   "F_Land_parcelsMapService",
  //   {
  //     opacity: 1,
  //     sublayers: [{ id: 1, definitionExpression: "Feature_Ad is null" }],
  //     minScale: 26111
  //   }
  // );
  // mapInstance.add(f_Land_parcelsMapService);

  // Land parcels layer
  let f_Land_parcels = createFeatureLayer(
    arcgisServicesLandFeatureUrl + "1",
    "F_Land_parcels",
    {
      opacity: 1,
      definitionExpression: OFFERS_BASE_WHERE,
      minScale: 26111,
    }
  );
  mapInstance.add(f_Land_parcels);

  // Flashlight layer
  let f_Land_parcelsFlashLight = createFeatureLayer(
    arcgisServicesLandFeatureUrl + "1",
    "f_Land_parcelsFlashLight",
    {
      opacity: 1,
      definitionExpression: "Feature_Ad ='true'"
    }
  );
  mapInstance.add(f_Land_parcelsFlashLight);

  // Heatmap layer
  let f_Land_parcelsHeatmap: any = undefined;
  f_Land_parcelsHeatmap = createFeatureLayer(
    arcgisServicesLandFeatureUrl + "2",
    "F_Land_parcelsHeatmap",
    {
      opacity: 1,
      definitionExpression: HEATMAP_BASE_WHERE,
      maxScale: 4513.9887055
    }
  );

  // Points of zoom out (heatmap)
  f_Land_parcelsHeatmap.renderer = uniqueValueRenderer;
  mapInstance.add(f_Land_parcelsHeatmap);

  // Favorite land layer
  const heartSymbol = new PictureMarkerSymbol({
    url: heartIcon,
    width: "24px",
    height: "24px"
  });

  const f_FavoriteLand = createFeatureLayer(
    arcgisServicesLandFeatureUrl + "0",
    "F_FavoriteLand",
    {
      opacity: 1,
      minScale: 76111,
      labelsVisible: false,
      renderer: new SimpleRenderer({ symbol: heartSymbol })
    }
  );
  mapInstance.add(f_FavoriteLand);
  return { f_Region, f_Land_parcels, f_Land_parcelsFlashLight, f_Land_parcelsHeatmap, f_FavoriteLand };
};

// Graphics layer creation
const initializeGraphicsLayers = () => {
  graphicsLayer = new GraphicsLayer();
  graphicsLayer.id = "graphicsLayer";

  graphicsLogoLayerlogoAdd = new GraphicsLayer();
  graphicsLogoLayerlogoAdd.minScale = 6000;
  graphicsLogoLayerlogoAdd.id = "graphicsLogoLayerlogoAdd";

  graphicsLogoLayer = new GraphicsLayer();
  graphicsLogoLayer.id = "graphicsLogoLayer";

  graphicsbuildingsWalletLayer = new GraphicsLayer();
  graphicsbuildingsWalletLayer.id = "graphicsbuildingsWalletLayer";
  routeLayer = new GraphicsLayer();
};

// Core map initialization
const initializeMap = async (containerId: string) => {
  const container = document.getElementById(containerId) as HTMLDivElement;
  if (!container) {
    console.error(`Container with ID '${containerId}' not found`);
    return null;
  }

  try {
    // Initialize map and layers
    map = new Map();
    initializeGraphicsLayers();

    // Initialize feature layers on window object
    // window.F_Land_parcelsFlashLight = new FeatureLayer({});
    // window.F_Land_parcels = new FeatureLayer({});
    // window.F_FavoriteLand = new FeatureLayer({});

    // Add tile layer
    const tileLayer = new VectorTileLayer({
      url: vectorTileServerNewMinistryUrl,
      id: "vectorTileServerNewMinistry"
    });
    map.add(tileLayer);
    map.add(routeLayer);

    // Create map view
    MapViewData = new MapView({
      container: container,
      map: map,
      center: [39.8258, 21.4225],
      zoom: 10,
    });

    // Set constraints
    MapViewData.constraints.rotationEnabled = true;
    MapViewData.ui.remove("zoom");
    MapViewData.constraints.minZoom = 5;


    // Initialize feature layers
    const { f_Region, f_Land_parcels, f_Land_parcelsFlashLight, f_Land_parcelsHeatmap, f_FavoriteLand } = initializeFeatureLayers(map);
    window.F_Region = f_Region;
    // Land Advertisement layer
    window.F_Land_parcels = f_Land_parcels;
    window.F_Land_parcelsFlashLight = f_Land_parcelsFlashLight;
    window.F_Land_parcelsHeatmap = f_Land_parcelsHeatmap;
    window.F_FavoriteLand = f_FavoriteLand;

    // Add graphics layers
    
    map.add(graphicsLayer);
    
    // Logo Sign
    map.add(graphicsLogoLayerlogoAdd);
    
    map.add(graphicsLogoLayer);
    
    // Wallet Sign
    map.add(graphicsbuildingsWalletLayer);

    window.graphicsLogoLayerlogoAdd = graphicsLogoLayerlogoAdd;
    window.graphicsLogoLayer = graphicsLogoLayer;
    window.graphicsbuildingsWalletLayer = graphicsbuildingsWalletLayer;

    return { map, MapViewData };
  } catch (error) {
    console.error('Error initializing map:', error);
    return null;
  }
};

// Utility functions
const createFeatureGraphics = (type: any, geometry: any, wkid: any, attr: any, symbolS: any) => {
  let fFeature: any;

  switch (type) {
    case "polygon":
      fFeature = new Polygon({
        rings: geometry,
        spatialReference: { wkid },
      });
      break;
    case "point":
      fFeature = new Point({
        x: geometry.x,
        y: geometry.y,
        spatialReference: { wkid },
      });
      break;
  }

  return new Graphic({
    geometry: fFeature,
    attributes: attr,
    symbol: symbolS,
  });
};

const zoomToLayer = (type: any, geometry: any, wiki: any, ZoomLevel: any) => {
  graphicsLayer.removeAll();

  const fGetGraphic = createFeatureGraphics(type, geometry, wiki, "", selectedSymbol);
  graphicsLayer.add(fGetGraphic);

  if (ZoomLevel) {
    MapViewData.goTo({
      target: fGetGraphic,
      zoom: 17,
    }, { duration: 5000 });
  } else {
    const gr = graphicsLayer.graphics;
    if (gr.length > 0) {
      MapViewData.goTo(gr.getItemAt(0).geometry.extent.expand(2));
    }
  }
};

// Layer visibility control
const toggleFavoriteIcons = (show: boolean) => {
  if (window.F_FavoriteLand) {
    window.F_FavoriteLand.visible = show;
  }
};

/**
 * Limit the advertisement layers to the checked property type / deal rows.
 *
 * Each layer keeps its own base filter, so the expressions are composed
 * separately. When nothing resolves to a filterable row there is nothing valid
 * to put in an IN clause, so the layers are hidden instead.
 */
const applyOfferFilters = (ads: MapAd[], selection: FilterSelection) => {
  const targets = [
    { layer: window.F_Land_parcels, baseWhere: OFFERS_BASE_WHERE },
    { layer: window.F_Land_parcelsHeatmap, baseWhere: HEATMAP_BASE_WHERE },
  ];

  targets.forEach(({ layer, baseWhere }) => {
    if (!layer) {
      return;
    }

    const where = buildOffersWhere(baseWhere, ads, selection);

    if (where === null) {
      layer.visible = false;
      return;
    }

    layer.definitionExpression = where;
    layer.visible = true;
  });
};

const toggleWalletIcons = (show: boolean) => {
  if (window.graphicsbuildingsWalletLayer) {
    window.graphicsbuildingsWalletLayer.visible = show;
  }
};

// Regions data fetching
const getRegions = async () => {
  try {
    const response = await fetch(arcgisServicesLandFeatureUrl + "3/query?f=json&where=1%3D1&outFields=*&returnGeometry=true");
    const data = await response.json();

    if (data.features && data.features.length > 0 && data.features[0].geometry) {
      return data.features.map((result: any, index: number) => ({
        name: result.attributes.REGIONNAME_AR != null
          ? result.attributes.REGIONNAME_AR
          : "بدون اسم",
        key: result.attributes.REGION_ID ?? index,
        graphic: result.geometry,
      }));
    }
    return [];
  } catch (error) {
    console.error("Error fetching regions:", error);
    return [];
  }
};

export const useMapLogic = () => {
  return {
    initializeMap,
    map,
    MapViewData,
    graphicsLayer,
    graphicsLogoLayerlogoAdd,
    graphicsLogoLayer,
    graphicsbuildingsWalletLayer,
    routeLayer,
    getRegions,
    createFeatureGraphics,
    zoomToLayer,
    toggleFavoriteIcons,
    applyOfferFilters,
    toggleWalletIcons,
  };
};
